// apps/web/src/pages/definir-senha-page.tsx — Fase 06.
//
// Página pública que consome o link de definição de senha (setupLink) gerado ao
// criar um usuário, ou um link de reset. Lê o token da URL (?token=...), coleta
// a nova senha e chama POST /api/v1/auth/password/reset/confirm (endpoint já
// existente, @Public). Nenhum dado sensível vive em Web Storage (SEC-001).
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { apiMutate } from '../lib/csrf-fetch';
import '../styles/login.css';
import { AuthLayout } from '../components/auth/auth-layout';

const SENHA_MIN = 12;

type Fase =
  | { readonly kind: 'form' }
  | { readonly kind: 'enviando' }
  | { readonly kind: 'ok' }
  | { readonly kind: 'erro'; readonly message: string };

function tokenDaUrl(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('token')?.trim() ?? '';
}

export function DefinirSenhaPage(): ReactElement {
  const token = useMemo(tokenDaUrl, []);
  const [senha, setSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [fase, setFase] = useState<Fase>({ kind: 'form' });
  const senhaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) {
      setFase({ kind: 'erro', message: 'Link inválido ou incompleto. Solicite um novo link ao administrador.' });
    } else {
      senhaRef.current?.focus();
    }
  }, [token]);

  const enviando = fase.kind === 'enviando';

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (senha.length < SENHA_MIN) {
      setFase({ kind: 'erro', message: `A senha precisa ter no mínimo ${SENHA_MIN} caracteres.` });
      return;
    }
    if (senha !== confirmar) {
      setFase({ kind: 'erro', message: 'As senhas não coincidem.' });
      return;
    }
    setFase({ kind: 'enviando' });
    try {
      const res = await apiMutate('/api/v1/auth/password/reset/confirm', {
        body: { token, newPassword: senha },
      });
      if (res.ok) {
        setFase({ kind: 'ok' });
        return;
      }
      const data = (await res.json().catch(() => ({}))) as { message?: string };
      setFase({
        kind: 'erro',
        message:
          res.status === 400
            ? data.message ?? 'Senha fora da política (mínimo 12 caracteres e não pode ser uma senha vazada).'
            : 'Não foi possível definir a senha. O link pode ter expirado — solicite um novo ao administrador.',
      });
    } catch {
      setFase({ kind: 'erro', message: 'Falha de conexão. Verifique sua internet e tente novamente.' });
    }
  }

  return (
    <AuthLayout>
        <p className="login-eyebrow">ACESSO À PLATAFORMA</p>
        <h1 className="login-page__title login-page__title--sr">Definir senha</h1>
        <h2 className="login-page__subtitle">Definir senha</h2>

        {fase.kind === 'ok' ? (
          <div role="status" aria-live="polite" className="login-form__success">
            <h2 className="login-form__heading">Senha definida com sucesso</h2>
            <p>Você já pode entrar no sistema com sua nova senha.</p>
            <a className="ui-button ui-button--primary ui-button--full-width" href="/login">
              Ir para o login
            </a>
          </div>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} className="login-form" aria-busy={enviando} noValidate>
            {fase.kind === 'erro' && (
              <div role="alert" className="ui-form-message login-form__alert">
                {fase.message}
                <p className="mt-3"><a href="/login" className="underline">Voltar ao login</a> · Solicite um novo convite ao administrador.</p>
              </div>
            )}

            <p className="login-form__hint">Crie uma senha de no mínimo {SENHA_MIN} caracteres.</p>

            <div className="login-form__field">
              <label htmlFor="nova-senha" className="login-form__label">Nova senha</label>
              <div className="login-form__password-wrapper">
                <input
                  id="nova-senha"
                  ref={senhaRef}
                  type={mostrar ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="ui-input ui-control"
                  disabled={enviando || !token}
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrar((v) => !v)}
                  aria-label={mostrar ? 'Ocultar caracteres' : 'Exibir caracteres'}
                  className="ui-button ui-button--secondary login-form__password-toggle"
                  disabled={enviando}
                >
                  {mostrar ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="login-form__field">
              <label htmlFor="confirmar-senha" className="login-form__label">Confirmar senha</label>
              <input
                id="confirmar-senha"
                type={mostrar ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmar}
                onChange={(e) => setConfirmar(e.target.value)}
                className="ui-input ui-control"
                disabled={enviando || !token}
                required
              />
            </div>

            <button
              type="submit"
              className="ui-button ui-button--primary ui-button--full-width"
              disabled={enviando || !token}
            >
              {enviando ? (
                <>
                  <Loader2 size={18} aria-hidden="true" className="login-form__spinner" /> Salvando…
                </>
              ) : (
                'Definir senha'
              )}
            </button>
          </form>
        )}
    </AuthLayout>
  );
}
